/**
 * Canonical URL normalization — single source of truth.
 *
 * Used by DomainSEO (server canonical tag) AND UrlNormalizer (client redirect)
 * so that the canonical tag and the actual served URL always agree.
 *
 * Rules applied in order:
 *  1. Lowercase the path
 *  2. Strip trailing slash (except root "/")
 *  3. /roofing/roofing-<slug>  → /roofing/<slug>
 *  4. Collapse double slashes
 *  5. Map known legacy aliases (loser → winner) — Stage 2 V2.2 §6a + §6b ACTIVATED 2026-07-16
 */

const LEGACY_PATH_ALIASES: Record<string, string> = {
  // Non-redirect path fixes
  "/sitemap": "/sitemap.xml",
  "/blog/": "/blog",
  // Duplicate "about" pages (both indexed, both ranking) — /about wins
  "/about-us": "/about",
  "/roofing-services/": "/roofing-services",
  // Service-route consolidation (Stage 2 V2.2 §6a) — ACTIVATED 2026-07-16
  "/services/roof-repair": "/roof-repair",
  "/services/roof-replacement": "/roof-replacement",
  "/services/insurance-claim": "/roof-insurance-claims",
  "/services/emergency-roofing": "/emergency",
  "/services": "/roofing-services",
  "/florida-roofing-services": "/roofing-services",
  "/roofing": "/roofing-services",
  // Nested ↔ flat city consolidation (Stage 2 V2.2 §6b) — ACTIVATED 2026-07-16
  "/roofing/fort-lauderdale/lighthouse-point": "/roofing/lighthouse-point",
  "/roofing/fort-lauderdale/lauderhill": "/roofing/lauderhill",
  "/roofing/fort-lauderdale/wilton-manors": "/roofing/wilton-manors",
  "/roofing/west-park": "/roofing/hollywood/west-park",
  "/roofing/coral-springs/parkland": "/roofing/parkland",
  "/roofing/fort-lauderdale/cooper-city": "/roofing/cooper-city",
  "/roofing/fort-lauderdale/weston": "/roofing/weston",
  "/roofing/hollywood/hallandale-beach": "/roofing/hallandale-beach",
  "/roofing/fort-lauderdale/tamarac": "/roofing/tamarac",
  "/roofing/coral-springs/margate": "/roofing/margate",
  "/roofing/coral-springs/coconut-creek": "/roofing/coconut-creek",
  "/roofing/fort-lauderdale/sunrise": "/roofing/sunrise",
  "/roofing/pembroke-pines/miramar": "/roofing/miramar",
  "/roofing/fort-lauderdale/davie": "/roofing/davie",
  "/roofing/boca-raton/deerfield-beach": "/roofing/deerfield-beach",
  "/roofing/fort-lauderdale/pompano-beach": "/roofing/pompano-beach",
  "/roofing/fort-lauderdale/lauderdale-lakes": "/roofing/lauderdale-lakes",
  "/roofing/fort-lauderdale/north-lauderdale": "/roofing/north-lauderdale",
  "/roofing/fort-lauderdale/lauderdale-by-the-sea": "/roofing/lauderdale-by-the-sea",
  "/roofing/fort-lauderdale/oakland-park": "/roofing/oakland-park",
  "/roofing/hollywood/pembroke-park": "/roofing/pembroke-park",
  "/roofing/fort-lauderdale/southwest-ranches": "/roofing/southwest-ranches",
  "/roofing/fort-lauderdale/sea-ranch-lakes": "/roofing/sea-ranch-lakes",
  "/roofing/boca-raton/hillsboro-beach": "/roofing/hillsboro-beach",
  "/roofing/west-palm-beach/wellington": "/roofing/wellington",
  "/roofing/west-palm-beach/royal-palm-beach": "/roofing/royal-palm-beach",
  "/roofing/west-palm-beach/palm-beach-gardens": "/roofing/palm-beach-gardens",
  "/roofing/west-palm-beach/lake-worth-beach": "/roofing/lake-worth-beach",
  "/roofing/west-palm-beach/greenacres": "/roofing/greenacres",
  "/roofing/west-palm-beach/riviera-beach": "/roofing/riviera-beach",
  "/roofing/west-palm-beach/lantana": "/roofing/lantana",
  "/roofing/loxahatchee": "/roofing/west-palm-beach/loxahatchee",
  "/roofing/west-palm-beach/north-palm-beach": "/roofing/north-palm-beach",
  "/roofing/jupiter/tequesta": "/roofing/tequesta",
  "/roofing/jupiter/juno-beach": "/roofing/juno-beach",
  "/roofing/west-palm-beach/hypoluxo": "/roofing/hypoluxo",
  "/roofing/west-palm-beach/mangonia-park": "/roofing/mangonia-park",
  "/roofing/west-palm-beach/glen-ridge": "/roofing/glen-ridge",
  "/roofing/boca-raton/ocean-ridge": "/roofing/ocean-ridge",
  "/roofing/boca-raton/highland-beach": "/roofing/highland-beach",
  "/roofing/tampa/westchase": "/roofing/westchase",
  "/roofing/tampa/carrollwood": "/roofing/carrollwood",
  "/roofing/tampa/riverview": "/roofing/riverview",
  "/roofing/tampa/brandon": "/roofing/brandon",
  "/roofing/tampa/lutz": "/roofing/lutz",
  "/roofing/tampa/plant-city": "/roofing/plant-city",
  "/roofing/tampa/valrico": "/roofing/valrico",
  "/roofing/tampa/town-n-country": "/roofing/town-n-country",
  "/roofing/tampa/citrus-park": "/roofing/citrus-park",
  "/roofing/tampa/apollo-beach": "/roofing/apollo-beach",
  "/roofing/ruskin": "/roofing/tampa/ruskin",
  "/roofing/tampa/sun-city-center": "/roofing/sun-city-center",
  "/roofing/sarasota/palmer-ranch": "/roofing/palmer-ranch",
  "/roofing/sarasota/osprey": "/roofing/osprey",
  "/roofing/venice/nokomis": "/roofing/nokomis",
  "/roofing/bradenton/palmetto": "/roofing/palmetto",
  "/roofing/bradenton/parrish": "/roofing/parrish",
  "/roofing/bradenton/ellenton": "/roofing/ellenton",
  "/roofing/bradenton/holmes-beach": "/roofing/holmes-beach",
  "/roofing/fort-myers/lehigh-acres": "/roofing/lehigh-acres",
  "/roofing/fort-myers/sanibel": "/roofing/sanibel",
  "/roofing/naples/immokalee": "/roofing/immokalee",
  "/roofing/naples/ave-maria": "/roofing/ave-maria",
  "/roofing/englewood": "/roofing/port-charlotte/englewood",
  "/roofing/port-charlotte/rotonda-west": "/roofing/rotonda-west",
  "/roofing/punta-gorda/deep-creek": "/roofing/deep-creek",
  "/roofing/naples/pelican-bay": "/roofing/pelican-bay",
  "/roofing/naples/golden-gate": "/roofing/golden-gate",
  "/roofing/fort-myers/gateway": "/roofing/gateway",
  "/roofing/fort-myers/fort-myers-beach": "/roofing/fort-myers-beach",
  "/roofing/sarasota/siesta-key": "/roofing/siesta-key",
  "/roofing/sarasota/longboat-key": "/roofing/longboat-key",
  "/roofing/sarasota/gulf-gate": "/roofing/gulf-gate",
  "/roofing/bradenton/anna-maria": "/roofing/anna-maria",
  "/roofing/bradenton/lakewood-ranch": "/roofing/lakewood-ranch",
  "/roofing/naples/vineyards": "/roofing/vineyards",
  "/roofing/sarasota/southgate": "/roofing/southgate",
  "/roofing/sarasota/bee-ridge": "/roofing/bee-ridge",
  "/roofing/bradenton/bradenton-beach": "/roofing/bradenton-beach",
  "/roofing/fort-myers/north-fort-myers": "/roofing/north-fort-myers",
  "/roofing/port-charlotte/murdock": "/roofing/murdock",
};

export const normalizePath = (rawPath: string): string => {
  if (!rawPath) return "/";
  let path = rawPath;

  // Preserve known asset paths verbatim (sitemap.xml, robots.txt, etc.)
  if (/\.[a-z0-9]{2,5}$/i.test(path)) {
    return path;
  }

  // Lowercase
  path = path.toLowerCase();

  // Collapse multiple slashes
  path = path.replace(/\/{2,}/g, "/");

  // Direct legacy alias hit (with or without trailing slash)
  if (LEGACY_PATH_ALIASES[path]) return LEGACY_PATH_ALIASES[path];
  const noSlash = path.replace(/\/+$/, "");
  if (noSlash && LEGACY_PATH_ALIASES[noSlash]) return LEGACY_PATH_ALIASES[noSlash];

  // Strip trailing slash (except root)
  if (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1);
  }

  // Legacy city-URL generations, applied in order. Three generations shipped
  // live at once (/roofing/north-port-fl, /roofing/roofing-fish-hawk-fl,
  // /roofing/north-port) and split the equity across them. Everything folds
  // into the generation-3 form: /roofing/<slug>.
  //
  // Gen 2 prefix: /roofing/roofing-<slug> → /roofing/<slug>
  const prefixDup = path.match(/^\/roofing\/roofing-(.+)$/);
  if (prefixDup) path = `/roofing/${prefixDup[1]}`;

  // Gen 1 suffix: /roofing/<slug>-fl → /roofing/<slug>
  // Scoped to single-segment Florida slugs so /roofing/ga/<slug> and nested
  // city/neighborhood paths are untouched.
  const suffixDup = path.match(/^\/roofing\/([a-z0-9-]+)-fl$/);
  if (suffixDup) path = `/roofing/${suffixDup[1]}`;

  return path || "/";
};

/**
 * Canonical origin is ALWAYS obriencontractingusa.com for OBC,
 * regardless of which hostname (apex, www, preview) the request arrived on.
 */
export const buildCanonicalUrl = (
  canonicalOrigin: string,
  rawPath: string,
): string => {
  const path = normalizePath(rawPath);
  if (path === "/") return canonicalOrigin;
  return `${canonicalOrigin}${path}`;
};
